/**
 * RevenueCat Service
 *
 * Unified cross-platform subscription management using RevenueCat
 * Replaces platform-specific IAP implementations with single API
 *
 * @see docs/REVENUECAT_INTEGRATION_GUIDE.md for setup instructions
 */

import { Platform } from 'react-native';
import Purchases, {
  PurchasesOfferings,
  PurchasesPackage,
  CustomerInfo,
  PurchasesEntitlementInfo,
  PURCHASES_ERROR_CODE,
  PURCHASE_TYPE,
  LOG_LEVEL,
  PurchasesStoreProduct
} from 'react-native-purchases';

import { logAnalyticsEvent } from '../../../lib/firebase';
import { ALL_PRODUCT_IDS, getBillingInterval } from '../config/revenueCatProducts';
import { SubscriptionTier } from '../types/premiumFeatures';

// RevenueCat API Keys (from environment config)
// Can use a single public key for all platforms or separate keys
// IMPORTANT: For iOS, the key should start with 'appl_'
// IMPORTANT: For Android, the key should start with 'goog_'

// Resolve env var with Expo Constants fallback (for release builds where
// react-native-dotenv may not inject process.env values).
const resolveEnv = (key: string): string | undefined => {
  // 1. process.env (set by react-native-dotenv at build time)
  const envVal = process.env[key];
  if (envVal && envVal !== 'undefined') return envVal;
  // 2. Expo Constants extra (set in app.json / app.config.js)
  try {
    const Constants = require('expo-constants').default;
    const extra = Constants?.expoConfig?.extra ?? Constants?.manifest?.extra ?? {};
    const constVal = extra[key];
    if (constVal && constVal !== 'undefined') return String(constVal);
  } catch {}
  return undefined;
};

const pickRevenueCatApiKey = (): string => {
  const shared = resolveEnv('EXPO_PUBLIC_REVENUECAT_PUBLIC_KEY');
  const ios = resolveEnv('EXPO_PUBLIC_REVENUECAT_IOS_KEY');
  const android = resolveEnv('EXPO_PUBLIC_REVENUECAT_ANDROID_KEY');

  if (Platform.OS !== 'ios' && Platform.OS !== 'android') {
    return '';
  }

  if (Platform.OS === 'ios') {
    if (ios) return ios;
    if (shared?.startsWith('appl_')) return shared;
    if (shared) {
      // Avoid accidentally using an Android key on iOS (common env misconfig in CI/EAS).
      console.warn('⚠️ [RevenueCat] Ignoring EXPO_PUBLIC_REVENUECAT_PUBLIC_KEY on iOS because it does not start with "appl_".');
    }
    return '';
  }

  if (Platform.OS === 'android') {
    if (android) return android;
    if (shared?.startsWith('goog_')) return shared;
    if (shared) {
      console.warn('⚠️ [RevenueCat] Ignoring EXPO_PUBLIC_REVENUECAT_PUBLIC_KEY on Android because it does not start with "goog_".');
    }
    return '';
  }

  return '';
};

const REVENUECAT_API_KEY = pickRevenueCatApiKey();

// Debug: Log key availability only on native platforms where RevenueCat is used.
if (Platform.OS === 'ios' || Platform.OS === 'android') {
  console.log('📱 [RevenueCat] Platform:', Platform.OS);
  console.log('📱 [RevenueCat] API Key configured:', REVENUECAT_API_KEY ? `${REVENUECAT_API_KEY.slice(0, 10)}...` : 'NOT SET');
}

// Entitlement identifiers (configured in RevenueCat dashboard)
export const ENTITLEMENTS = {
  PREMIUM: 'premium',
} as const;

// Offering identifiers (configured in RevenueCat dashboard)
export const OFFERINGS = {
  DEFAULT: 'default',
} as const;

export interface RevenueCatSubscriptionInfo {
  isActive: boolean;
  tier: SubscriptionTier;
  expirationDate: Date | null;
  willRenew: boolean;
  billingInterval: 'monthly' | 'annual';
  productIdentifier: string;
  originalPurchaseDate: Date;
  entitlements: string[];
}

export interface RevenueCatOffering {
  identifier: string;
  serverDescription: string;
  packages: RevenueCatPackageInfo[];
}

export interface RevenueCatPackageInfo {
  identifier: string;
  packageType: string;
  product: {
    identifier: string;
    title: string;
    description: string;
    price: number;
    priceString: string;
    currencyCode: string;
    introPrice?: {
      price: number;
      priceString: string;
      period: string;
    };
  };
}

export interface PurchaseResult {
  success: boolean;
  customerInfo?: CustomerInfo;
  error?: string;
  userCancelled?: boolean;
}

const isPurchasesError = (error: unknown): error is { code?: string; message?: string } =>
  typeof error === 'object' && error !== null;

const getPurchasesErrorMessage = (error: unknown, fallback: string): string => {
  if (isPurchasesError(error) && typeof error.message === 'string') {
    return error.message;
  }

  return fallback;
};

class RevenueCatService {
  private isInitialized = false;
  private currentUserId: string | null = null;

  private async ensureInitialized(): Promise<void> {
    if (this.isInitialized) return;
    await this.initialize(this.currentUserId || undefined);
    if (!this.isInitialized) {
      // initialize() can no-op if keys are missing; surface a consistent error for callers.
      throw new Error('RevenueCat not initialized');
    }
  }

  /**
   * Initialize RevenueCat SDK
   * Must be called before any other methods
   */
  async initialize(userId?: string): Promise<void> {
    try {
      if (!this.isAvailable()) {
        return;
      }

      if (this.isInitialized) {
        console.log('📱 [RevenueCat] Already initialized');
        return;
      }

      if (!REVENUECAT_API_KEY) {
        console.error('❌ [RevenueCat] API key not configured!');
        console.error('📱 [RevenueCat] Please set EXPO_PUBLIC_REVENUECAT_PUBLIC_KEY or EXPO_PUBLIC_REVENUECAT_IOS_KEY');
        // Don't throw - allow app to continue without IAP functionality
        // This prevents crashes during App Store review if key is misconfigured
        this.isInitialized = false;
        return;
      }

      console.log('📱 [RevenueCat] Initializing with key:', REVENUECAT_API_KEY.slice(0, 10) + '...');

      // Configure SDK with debug logging in development
      if (__DEV__) {
        Purchases.setLogLevel(LOG_LEVEL.DEBUG);
      } else {
        Purchases.setLogLevel(LOG_LEVEL.WARN);
      }

      // Initialize SDK
      await Purchases.configure({
        apiKey: REVENUECAT_API_KEY,
        appUserID: userId, // Optional - RevenueCat generates anonymous ID if not provided
      });

      this.isInitialized = true;
      this.currentUserId = userId || null;

      // Apple Ads attribution (iOS only). RevenueCat reads Apple's AdServices
      // attribution token and ties this install — and every subscription that
      // follows it — back to the campaign/keyword that produced it. This is the
      // iOS counterpart to the Play install-referrer ping in
      // src/lib/installAttribution.ts.
      //
      // The AdServices token is privacy-preserving and needs no ATT prompt, so
      // there is no user-facing cost to collecting it. Failure here is never
      // fatal: attribution is measurement, and losing it must not take
      // purchasing down with it.
      // The success line is deliberate: it is the only device-side signal that
      // the opt-in actually fired. A TestFlight install can never attribute —
      // Apple answers `attribution: false` for every non-campaign install — so
      // watching for this line in the device log is the only verification
      // available until a production release is running live campaigns.
      if (Platform.OS === 'ios') {
        try {
          await Purchases.enableAdServicesAttributionTokenCollection();
          console.log('📊 [RevenueCat] Apple Ads attribution collection enabled');
        } catch (attributionError) {
          console.warn('⚠️ [RevenueCat] Apple Ads attribution unavailable:', attributionError);
        }
      }

      console.log('✅ [RevenueCat] Initialized successfully', {
        platform: Platform.OS,
        userId: userId ? `${userId.slice(0, 8)}...` : 'anonymous',
        apiKey: REVENUECAT_API_KEY.slice(0, 10) + '...'
      });

      // Set up customer info listener
      Purchases.addCustomerInfoUpdateListener((customerInfo) => {
        console.log('📱 [RevenueCat] Customer info updated:', {
          activeSubscriptions: customerInfo.activeSubscriptions,
          entitlements: Object.keys(customerInfo.entitlements.active)
        });
      });

    } catch (error) {
      console.error('❌ [RevenueCat] Failed to initialize:', error);
      // Mark as not initialized but don't crash
      this.isInitialized = false;
      // Re-throw so caller knows initialization failed
      throw error;
    }
  }

  /**
   * Identify user with RevenueCat
   * Call after user logs in to sync subscriptions across devices
   */
  async identifyUser(userId: string): Promise<void> {
    try {
      if (!this.isInitialized) {
        await this.initialize(userId);
        return;
      }

      console.log('🔑 Identifying user with RevenueCat:', userId.slice(0, 8) + '...');
      await Purchases.logIn(userId);
      this.currentUserId = userId;

    } catch (error) {
      console.error('❌ Failed to identify user:', error);
      throw error;
    }
  }

  /**
   * Log out user (call when user signs out)
   */
  async logout(): Promise<void> {
    try {
      if (!this.isInitialized) {
        return;
      }

      console.log('👋 Logging out user from RevenueCat');
      await Purchases.logOut();
      this.currentUserId = null;

    } catch (error) {
      console.error('❌ Failed to log out:', error);
      throw error;
    }
  }

  /**
   * Get available subscription offerings
   * Returns the single active consumer offering.
   */
  async getOfferings(): Promise<{
    current: RevenueCatOffering | null;
  }> {
    try {
      await this.ensureInitialized();

      console.log('📦 Fetching offerings from RevenueCat...');
      let offerings: PurchasesOfferings;
      try {
        // Helps when using targeting rules / subscriber attributes; safe otherwise.
        offerings = await Purchases.syncAttributesAndOfferingsIfNeeded();
      } catch (syncError) {
        console.warn('⚠️ [RevenueCat] syncAttributesAndOfferingsIfNeeded failed, falling back to getOfferings:', syncError);
        offerings = await Purchases.getOfferings();
      }

      console.log('📦 Raw offerings response:', {
        current: offerings.current?.identifier || 'none',
        allOfferingIds: Object.keys(offerings.all),
        currentPackages: offerings.current?.availablePackages?.length || 0
      });

      // Prefer the default offering, then fall back to current/first available.
      let currentOffering = offerings.all[OFFERINGS.DEFAULT];

      if (!currentOffering && offerings.current) {
        console.log('📦 Using current offering as default offering fallback');
        currentOffering = offerings.current;
      }

      if (!currentOffering && Object.keys(offerings.all).length > 0) {
        const firstOfferingKey = Object.keys(offerings.all)[0];
        console.log(`📦 Using first available offering "${firstOfferingKey}" as fallback`);
        currentOffering = offerings.all[firstOfferingKey];
      }

      console.log('✅ Offerings fetched:', {
        current: currentOffering ? `${currentOffering.availablePackages.length} packages` : 'none',
      });

      return {
        current: currentOffering ? this.convertOffering(currentOffering) : null,
      };

    } catch (error) {
      console.error('❌ Failed to fetch offerings:', error);
      throw error;
    }
  }

  /**
   * Fallback: fetch products directly (without relying on Offerings).
   *
   * This is useful when Offerings are misconfigured, but StoreKit products exist.
   */
  async getFallbackProducts(productIds: string[] = ALL_PRODUCT_IDS): Promise<PurchasesStoreProduct[]> {
    await this.ensureInitialized();
    try {
      console.log('🧾 [RevenueCat] Fetching fallback products:', productIds.length);
      const products = await Purchases.getProducts(productIds);
      console.log('✅ [RevenueCat] Fallback products fetched:', products.length);
      return products;
    } catch (error) {
      console.error('❌ [RevenueCat] Failed to fetch fallback products:', error);
      return [];
    }
  }

  async getStorefrontCountryCode(): Promise<string | null> {
    await this.ensureInitialized();
    try {
      const storefront = await Purchases.getStorefront();
      return storefront?.countryCode ?? null;
    } catch (error) {
      console.warn('⚠️ [RevenueCat] Failed to fetch storefront:', error);
      return null;
    }
  }

  /**
   * Purchase a StoreKit product directly (fallback path when packages/offerings are unavailable).
   */
  async purchaseStoreProduct(product: PurchasesStoreProduct): Promise<PurchaseResult> {
    // GA4 recommended event names (begin_checkout / purchase) so Firebase's built-in
    // funnel and monetization reports work without configuration. RevenueCat stays the
    // source of truth for revenue; this exists to see WHERE the funnel leaks:
    // paywall_view -> begin_checkout -> purchase / purchase_cancelled / purchase_failed.
    const funnel = {
      item_id: product.identifier,
      currency: product.currencyCode,
      value: typeof product.price === 'number' ? product.price : undefined,
      platform: Platform.OS,
    };
    try {
      await this.ensureInitialized();
      void logAnalyticsEvent('begin_checkout', funnel);

      const productCategory = 'productCategory' in product
        ? (product as PurchasesStoreProduct & { productCategory?: PURCHASE_TYPE }).productCategory
        : undefined;

      console.log('💳 [RevenueCat] Initiating store product purchase:', {
        product: product.identifier,
        price: product.priceString,
        productCategory: productCategory || 'missing',
      });

      const { customerInfo } = !productCategory
        ? Platform.OS === 'android'
          ? await Purchases.purchaseProduct(product.identifier, null, PURCHASE_TYPE.SUBS)
          : await Purchases.purchaseProduct(product.identifier)
        : await Purchases.purchaseStoreProduct(product);

      console.log('✅ [RevenueCat] Store product purchase successful!', {
        activeSubscriptions: customerInfo.activeSubscriptions,
        entitlements: Object.keys(customerInfo.entitlements.active)
      });

      void logAnalyticsEvent('purchase', funnel);
      return { success: true, customerInfo };
    } catch (error: unknown) {
      if (isPurchasesError(error) && error.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
        console.log('🚫 [RevenueCat] User cancelled store product purchase');
        void logAnalyticsEvent('purchase_cancelled', { item_id: funnel.item_id, platform: funnel.platform });
        return {
          success: false,
          userCancelled: true,
          error: 'Purchase cancelled by user'
        };
      }

      console.error('❌ [RevenueCat] Store product purchase failed:', error);
      void logAnalyticsEvent('purchase_failed', {
        item_id: funnel.item_id,
        platform: funnel.platform,
        reason: isPurchasesError(error) ? String(error.code) : 'unknown',
      });
      return {
        success: false,
        error: getPurchasesErrorMessage(error, 'Purchase failed')
      };
    }
  }

  /**
   * Purchase a subscription package
   */
  async purchasePackage(pkg: PurchasesPackage): Promise<PurchaseResult> {
    try {
      await this.ensureInitialized();

      console.log('💳 Initiating purchase:', {
        package: pkg.identifier,
        product: pkg.product.identifier,
        price: pkg.product.priceString
      });

      // The UI uses a serialized package shape returned by convertOffering().
      // Purchasing by store product keeps the same entitlement behavior and
      // avoids native-package identity issues after serialization.
      return this.purchaseStoreProduct(pkg.product);

    } catch (error: unknown) {
      // Handle user cancellation
      if (isPurchasesError(error) && error.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
        console.log('🚫 User cancelled purchase');
        return {
          success: false,
          userCancelled: true,
          error: 'Purchase cancelled by user'
        };
      }

      // Handle other errors
      console.error('❌ Purchase failed:', error);
      return {
        success: false,
        error: getPurchasesErrorMessage(error, 'Purchase failed')
      };
    }
  }

  /**
   * Restore previous purchases
   * Important for users who reinstalled the app or switched devices
   */
  async restorePurchases(): Promise<PurchaseResult> {
    try {
      await this.ensureInitialized();

      console.log('🔄 Restoring purchases...');
      const customerInfo = await Purchases.restorePurchases();

      const hasActiveSubscription = Object.keys(customerInfo.entitlements.active).length > 0;

      console.log('✅ Restore complete:', {
        hasActiveSubscription,
        activeSubscriptions: customerInfo.activeSubscriptions,
        entitlements: Object.keys(customerInfo.entitlements.active)
      });

      return {
        success: true,
        customerInfo
      };

    } catch (error: unknown) {
      console.error('❌ Restore failed:', error);
      return {
        success: false,
        error: getPurchasesErrorMessage(error, 'Restore failed')
      };
    }
  }

  /**
   * Get current customer info and subscription status
   */
  async getCustomerInfo(): Promise<CustomerInfo | null> {
    try {
      await this.ensureInitialized();

      const customerInfo = await Purchases.getCustomerInfo();
      return customerInfo;

    } catch (error) {
      console.error('❌ Failed to get customer info:', error);
      return null;
    }
  }

  /**
   * Check if user has active subscription
   */
  async hasActiveSubscription(): Promise<boolean> {
    try {
      const customerInfo = await this.getCustomerInfo();
      if (!customerInfo) return false;

      return Object.keys(customerInfo.entitlements.active).length > 0;

    } catch (error) {
      console.error('❌ Failed to check subscription status:', error);
      return false;
    }
  }

  /**
   * Get subscription information
   * Returns null if no active subscription
   */
  async getSubscriptionInfo(): Promise<RevenueCatSubscriptionInfo | null> {
    try {
      const customerInfo = await this.getCustomerInfo();
      if (!customerInfo) return null;

      // Check for premium entitlement
      const premiumEntitlement = customerInfo.entitlements.active[ENTITLEMENTS.PREMIUM];
      if (!premiumEntitlement) return null;

      // Determine tier based on entitlements
      const tier = this.determineTier(customerInfo);

      // Get billing interval from product identifier
      const billingInterval = getBillingInterval(premiumEntitlement.productIdentifier);

      return {
        isActive: premiumEntitlement.isActive,
        tier,
        expirationDate: premiumEntitlement.expirationDate
          ? new Date(premiumEntitlement.expirationDate)
          : null,
        willRenew: premiumEntitlement.willRenew,
        billingInterval,
        productIdentifier: premiumEntitlement.productIdentifier,
        originalPurchaseDate: new Date(premiumEntitlement.originalPurchaseDate),
        entitlements: Object.keys(customerInfo.entitlements.active)
      };

    } catch (error) {
      console.error('❌ Failed to get subscription info:', error);
      return null;
    }
  }

  /**
   * Check if user has specific entitlement
   */
  async hasEntitlement(entitlement: string): Promise<boolean> {
    try {
      const customerInfo = await this.getCustomerInfo();
      if (!customerInfo) return false;

      return !!customerInfo.entitlements.active[entitlement];

    } catch (error) {
      console.error('❌ Failed to check entitlement:', error);
      return false;
    }
  }

  /**
   * Get subscription management URL
   * Directs user to platform-specific subscription management
   */
  async getManagementURL(): Promise<string> {
    try {
      await this.ensureInitialized();

      const customerInfo = await Purchases.getCustomerInfo();

      // RevenueCat provides management URL
      return customerInfo.managementURL || this.getPlatformManagementURL();

    } catch (error) {
      console.error('❌ Failed to get management URL:', error);
      return this.getPlatformManagementURL();
    }
  }

  /**
   * Helper: Convert RevenueCat offering to app format
   */
  private convertOffering(offering: NonNullable<PurchasesOfferings['current']>): RevenueCatOffering {
    return {
      identifier: offering.identifier,
      serverDescription: offering.serverDescription,
      packages: offering.availablePackages.map((pkg: PurchasesPackage) => ({
        identifier: pkg.identifier,
        packageType: pkg.packageType,
        product: {
          identifier: pkg.product.identifier,
          title: pkg.product.title,
          description: pkg.product.description,
          price: pkg.product.price,
          priceString: pkg.product.priceString,
          currencyCode: pkg.product.currencyCode,
          introPrice: pkg.product.introPrice ? {
            price: pkg.product.introPrice.price,
            priceString: pkg.product.introPrice.priceString,
            period: pkg.product.introPrice.period
          } : undefined
        }
      }))
    };
  }

  /**
   * Helper: Determine subscription tier from entitlements
   */
  private determineTier(customerInfo: CustomerInfo): SubscriptionTier {
    const entitlements = customerInfo.entitlements.active;

    if (entitlements[ENTITLEMENTS.PREMIUM]) {
      return SubscriptionTier.PREMIUM;
    }

    return SubscriptionTier.FREE;
  }

  /**
   * Helper: Get platform-specific management URL
   */
  private getPlatformManagementURL(): string {
    if (Platform.OS === 'ios') {
      return 'https://apps.apple.com/account/subscriptions';
    } else if (Platform.OS === 'android') {
      return 'https://play.google.com/store/account/subscriptions';
    }
    return '';
  }

  /**
   * Check if RevenueCat is available on current platform
   */
  isAvailable(): boolean {
    return Platform.OS === 'ios' || Platform.OS === 'android';
  }

  /**
   * Get debug information
   */
  async getDebugInfo(): Promise<{
    isInitialized: boolean;
    userId: string | null;
    platform: string;
    hasApiKey: boolean;
    customerInfo: CustomerInfo | null;
  }> {
    return {
      isInitialized: this.isInitialized,
      userId: this.currentUserId,
      platform: Platform.OS,
      hasApiKey: !!REVENUECAT_API_KEY,
      customerInfo: await this.getCustomerInfo()
    };
  }
}

// Export singleton instance
export const revenueCatService = new RevenueCatService();

// Export types
export type { CustomerInfo, PurchasesPackage, PurchasesEntitlementInfo };

// Default export
export default revenueCatService;
